import React, { FormEvent, useContext, useState, useEffect } from 'react';
import { WorkflowContext } from "../../context/WorkflowContextProvider";
import Form from '@rjsf/mui';
import { IChangeEvent } from '@rjsf/core';
import { RJSFSchema, UiSchema, RegistryWidgetsType } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { DefaultFormDialog  } from "./components/DefaultFormDialog";
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    useRecordContext,
    useCreate,
    useUpdate,
    useNotify,
    useDelete,
    usePermissions,
    Confirm
} from 'react-admin';
import { permissionManager } from "../../../permissions/manager";
import {
    Button
} from '@mui/material';

export const WorkflowExecutionConfiguration = () => {
    const context: any = useContext(WorkflowContext);
    const notify = useNotify();

    const [formData, setFormData] = useState<any>();
    const record = useRecordContext();
    const [create] = useCreate();
    const [update] = useUpdate();
    const [deleteOne, { isPending, error }] = useDelete();
    const { permissions } = usePermissions();
    const [confirmingClear, setConfirmingClear] = useState<boolean>(false);
    // Deleting the WorkflowExecution record, which is what the schedule is. The entity is gated on
    // HQ_System::workflows, so the verb is the one this button performs.
    const canClear: boolean = permissionManager.isAllowed(permissions, 'HQ_System::workflows.delete');
    function handleDialogClose(): void {
        context.toggleWorkflowExecutionSettingsModal(false);
    }

    const handleFormSubmit = (data: IChangeEvent, event: FormEvent<any>) : void => {
        if (record.workflowExecution !== undefined) {
            update(
                'workflow_executions',
                { id: record.id, data: data.formData, previousData: record },
                {
                    onSuccess: () => {
                        notify('Execution Updated', { type: 'success' });
                    },
                    onError: () => {
                        notify('Execution Failed', { type: 'error' });
                    }
                }
            );
        } else {
            data.formData.workflow = '/hq_api/workflows/' + record.id;
            create(
                'workflow_executions',
                { data: data.formData },
                {
                    onSuccess: () => {
                        notify('Execution Created', { type: 'success' });
                    },
                    onError: () => {
                        notify('Execution Failed', { type: 'error' });
                    }
                }
            )
        }

        handleDialogClose();
    }

    // Two things this had neither of. It destroys the workflow's schedule with one click, and it
    // is reached through a drawer rather than through react-admin's own delete button, so nothing
    // asked `canAccess` for it and nothing asked the admin either.
    function handleClearExecution(): void {
        setConfirmingClear(false);
        deleteOne(
            'workflow_executions',
            { id: record.workflowExecution, previousData: record }
        );
        handleDialogClose();
    }

    useEffect( () => {
        if (context.workflowExecutionSettings !== undefined) {
            setFormData(context.workflowExecutionSettings);
        }
    } , [context] );

    const formSchema: RJSFSchema = {
        title: '',
        type: 'object',
        required: [],
        properties: {
            name: {
                title: 'Execution Name',
                type: 'string',
                default: (record ? record.name + ' Execution' : '')
            },
            cronExpression: {
                title: "Cron Expression",
                type: 'string',
            }
        },
    }

    const description = "" +
        "Configure when your execution to run at time intervals. \n\n" +
        "The Cron Expression field takes standard cron values or hashed cron values, e.g:\n" +
        "- #hourly\t\t\t# * * * * (at some minute every hour)\n" +
        "- #daily\t\t\t# # * * * (at some time every day)\n" +
        "- #weekly\t\t\t# # * * # (at some time every week)\n" +
        "- #weekly@midnight\t\t# #(0-2) * * # (at #midnight one day every week)\n" +
        "- #monthly\t\t\t# # # * * (at some time on some day, once per month)\n" +
        "- #monthly@midnight\t\t# #(0-2) # * * (at #midnight on some day, once per month)\n" +
        "- #annually\t\t\t# # # # * (at some time on some day, once per year)\n" +
        "- #annually@midnight\t\t# #(0-2) # # * (at #midnight on some day, once per year)\n" +
        "- #yearly\t\t\t# # # # * alias for #annually\n" +
        "- #yearly@midnight\t\t# #(0-2) # # * alias for #annually@midnight\n" +
        "- #midnight\t\t\t# #(0-2) * * * (at some time between midnight and 2:59am, every day)\n";

    return (
        <DefaultFormDialog
            open={ context.showWorkflowExecutionSettingsModal }
            title={ "Execution Configuration" }
            handleClose={ handleDialogClose }
            description={ description }
        >
            <Form
                validator={validator}
                schema={formSchema}
                onSubmit={ handleFormSubmit }
                formData={ formData }
                formContext={ formData }
                onChange={({ formData }) => setFormData(formData)}
            >
                <div className="dialog__actions form-group">
                    <Button type="submit"
                            variant="contained"
                            color="success"
                    >
                        <SaveIcon />
                        <span>{ "Save" }</span>
                    </Button>
                    { canClear &&
                        <Button
                            variant="contained"
                            color="error"
                            onClick={ (): void => setConfirmingClear(true) }
                            type="button">
                            <span>{ "Clear" }</span>
                        </Button>
                    }
                    <Confirm
                        isOpen={ confirmingClear }
                        loading={ isPending }
                        title="Clear this schedule?"
                        content="The workflow keeps running when something triggers it, but it will no longer run on a schedule. This cannot be undone."
                        onConfirm={ handleClearExecution }
                        onClose={ (): void => setConfirmingClear(false) }
                    />
                </div>
            </Form>
        </DefaultFormDialog>
    );
}